import { loadSession } from "./session.js";
import { listMessages, listChanges, type ApiMessage } from "./api.js";

// Revision 6, Decision 1d: replaces `polling.ts`'s ~5-8s interval with a real
// persistent WebSocket connection to the same-origin `server/` process
// (proxied by Vite in dev, by Caddy in prod — see vite.config.ts's comment).
// Same call signature as the old `startPolling` so `Chat.tsx`'s call site
// change is a one-line swap.
//
// Reconnect/backoff + catch-up (per the handoff's Risks section: "a dropped
// connection needs both ws.ts clients to reconnect cleanly and re-sync any
// messages missed while disconnected"): on every successful (re)connect,
// this calls the same `GET /api/messages?since=` catch-up endpoint the old
// polling client used, so a gap while offline is always filled by the REST
// fallback shape, not silently dropped.

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 15000;
const MESSAGE_CATCHUP_LIMIT = 50;
const CHANGES_LIMIT = 200;

function maxUpdatedAt(messages: ApiMessage[]): number {
  return messages.reduce((max, m) => Math.max(max, m.updatedAt ?? m.createdAt), 0);
}

export interface WsHandlers {
  onMessageUpdate?: (messages: ApiMessage[]) => void;
}

function wsUrl(): string {
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  const session = loadSession();
  const token = session ? encodeURIComponent(session.token) : "";
  return `${protocol}//${window.location.host}/ws?token=${token}`;
}

export function startWs(
  onMessages: (messages: ApiMessage[]) => void,
  onError?: (err: unknown) => void,
  initialSince = 0,
  handlers?: WsHandlers,
  initialChangesCursor = 0
): () => void {
  let since = initialSince;
  let changesCursor = initialChangesCursor;
  let stopped = false;
  let socket: WebSocket | null = null;
  let reconnectDelay = RECONNECT_BASE_MS;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  function dispatchUpdates(messages: ApiMessage[]) {
    if (handlers?.onMessageUpdate) handlers.onMessageUpdate(messages);
    else onMessages(messages);
  }

  async function catchUp() {
    try {
      for (;;) {
        const result = await listMessages(since, MESSAGE_CATCHUP_LIMIT);
        if (result.messages.length === 0) break;
        since = result.messages[result.messages.length - 1]!.createdAt;
        changesCursor = Math.max(changesCursor, maxUpdatedAt(result.messages));
        onMessages(result.messages);
        if (result.messages.length < MESSAGE_CATCHUP_LIMIT) break;
      }
      for (;;) {
        const result = await listChanges(changesCursor, CHANGES_LIMIT);
        if (result.messages.length === 0) break;
        const last = result.messages[result.messages.length - 1]!;
        changesCursor = last.updatedAt ?? last.createdAt;
        dispatchUpdates(result.messages);
        if (result.messages.length < CHANGES_LIMIT) break;
      }
    } catch (err) {
      onError?.(err);
    }
  }

  function scheduleReconnect() {
    if (stopped) return;
    reconnectTimer = setTimeout(() => {
      reconnectDelay = Math.min(reconnectDelay * 2, RECONNECT_MAX_MS);
      connect();
    }, reconnectDelay);
  }

  function connect() {
    if (stopped) return;
    const session = loadSession();
    if (!session) return;

    socket = new WebSocket(wsUrl());

    socket.onopen = () => {
      reconnectDelay = RECONNECT_BASE_MS;
      void catchUp();
    };

    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data as string) as { type: string; message?: ApiMessage };
        if (data.type === "message" && data.message) {
          since = Math.max(since, data.message.createdAt);
          changesCursor = Math.max(changesCursor, data.message.updatedAt ?? data.message.createdAt);
          onMessages([data.message]);
        } else if (data.type === "message_update" && data.message) {
          changesCursor = Math.max(changesCursor, data.message.updatedAt ?? data.message.createdAt);
          dispatchUpdates([data.message]);
        }
      } catch (err) {
        onError?.(err);
      }
    };

    socket.onerror = (err) => {
      onError?.(err);
    };

    socket.onclose = () => {
      if (!stopped) scheduleReconnect();
    };
  }

  connect();

  return () => {
    stopped = true;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    socket?.close();
  };
}
